import React, { useEffect, useState, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { Plus, Search, Edit2, Eye, Trash2, Printer } from 'lucide-react';

const CATEGORIES = [
  { val:'TOOLS',      label:'Tools',            icon:'🔧' },
  { val:'MACHINERY',  label:'Machinery',         icon:'⚙️' },
  { val:'VEHICLE',    label:'Vehicle',           icon:'🚗' },
  { val:'ELECTRICAL', label:'Electrical',        icon:'⚡' },
  { val:'SAFETY',     label:'Safety Equipment',  icon:'🦺' },
  { val:'FURNITURE',  label:'Furniture',         icon:'🪑' },
  { val:'OTHER',      label:'Other',             icon:'📦' },
];

const CONDITIONS = [
  { val:'GOOD',    label:'Good',    color:'#3B6D11', bg:'#EAF3DE' },
  { val:'FAIR',    label:'Fair',    color:'#854F0B', bg:'#FAEEDA' },
  { val:'POOR',    label:'Poor',    color:'#C0392B', bg:'#FDE8E8' },
  { val:'DAMAGED', label:'Damaged', color:'#C0392B', bg:'#FDE8E8' },
];

const LOCATIONS = [
  { val:'QUARRY',   label:'Quarry' },
  { val:'OFFICE',   label:'Office' },
  { val:'WORKSHOP', label:'Workshop' },
  { val:'STORE',    label:'Store Room' },
  { val:'OTHER',    label:'Other' },
];

const GST_RATES    = ['0','5','12','18','28'];
const PAYMENT_MODES = ['CASH','BANK','CHEQUE','UPI'];
const UNITS        = ['NOS','SET','PAIR','KG','METER','LITRE','BOX','OTHER'];

const inr    = v => `₹${Number(v||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`;
const today  = new Date().toISOString().split('T')[0];
const getCat  = v => CATEGORIES.find(c=>c.val===v);
const getCond = v => CONDITIONS.find(c=>c.val===v);
const getLoc  = v => LOCATIONS.find(l=>l.val===v);
const isExpired = d => d && new Date(d) < new Date();
const isDue     = d => d && new Date(d) <= new Date();

const EMPTY = {
  name:'', category:'TOOLS', description:'',
  quantity:'1', unit:'NOS', unit_price:'', gst_percent:'18',
  purchase_date:today, vendor_name:'',
  payment_mode:'CASH', payment_reference:'', payment_date:today,
  condition:'GOOD', location:'QUARRY', assigned_to:'',
  warranty_expiry:'', next_maintenance:'',
  notes:'', is_active:true, also_record_expense:false,
};

const Field = ({ label, required, full, children }) => (
  <div className="form-group" style={full?{gridColumn:'1/-1'}:{}}>
    <label className="form-label">{label}{required&&<span style={{color:'var(--red)',marginLeft:3}}>*</span>}</label>
    {children}
  </div>
);

const ST = ({ children }) => (
  <div style={{fontSize:11,fontWeight:700,letterSpacing:'1.2px',textTransform:'uppercase',
    color:'var(--brand)',borderBottom:'2px solid var(--border)',paddingBottom:6,marginBottom:14,marginTop:4}}>
    {children}
  </div>
);

export default function AssetPage() {
  const [assets, setAssets]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterCat, setFilterCat]   = useState('');
  const [filterCond, setFilterCond] = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [showView, setShowView]     = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [selected, setSelected]     = useState(null);
  const [form, setForm]             = useState(EMPTY);
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState('');
  const [error, setError]           = useState('');
  const [employees, setEmployees]   = useState([]);
  const printRef = useRef();

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const qty      = parseFloat(form.quantity||0);
  const unitPrice= parseFloat(form.unit_price||0);
  const gstPct   = parseFloat(form.gst_percent||0);
  const subtotal = qty * unitPrice;
  const gstAmt   = subtotal * gstPct / 100;
  const total    = subtotal + gstAmt;

  const load = useCallback(() => {
    setLoading(true);
    let url = `/assets/?page_size=200`;
    if(search)     url += `&search=${search}`;
    if(filterCat)  url += `&category=${filterCat}`;
    if(filterCond) url += `&condition=${filterCond}`;
    api.get(url).then(r=>setAssets(r.data.results||[])).finally(()=>setLoading(false));
  }, [search, filterCat, filterCond]);

  useEffect(()=>{ load(); }, [load]);
  useEffect(()=>{
    api.get('/employees/?page_size=100&is_active=true').then(r=>setEmployees(r.data.results||[]));
  }, []);

  const openAdd = () => { setForm(EMPTY); setSelected(null); setError(''); setShowModal(true); };

  const openEdit = asset => {
    setForm({
      name:              asset.name             || '',
      category:          asset.category         || 'TOOLS',
      description:       asset.description      || '',
      quantity:          String(asset.quantity  || 1),
      unit:              asset.unit             || 'NOS',
      unit_price:        asset.unit_price       ? String(parseFloat(asset.unit_price)) : '',
      gst_percent:       asset.gst_percent      || '18',
      purchase_date:     asset.purchase_date    || today,
      vendor_name:       asset.vendor_name      || '',
      payment_mode:      asset.payment_mode     || 'CASH',
      payment_reference: asset.payment_reference|| '',
      payment_date:      asset.payment_date     || today,
      condition:         asset.condition        || 'GOOD',
      location:          asset.location         || 'QUARRY',
      assigned_to:       asset.assigned_to      || '',
      warranty_expiry:   asset.warranty_expiry  || '',
      next_maintenance:  asset.next_maintenance || '',
      notes:             asset.notes            || '',
      is_active:         asset.is_active !== undefined ? asset.is_active : true,
      also_record_expense: false,
    });
    setSelected(asset); setError(''); setShowModal(true);
  };

  const handleDelete = async asset => {
    if(!window.confirm(`Delete asset ${asset.asset_number} — ${asset.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/assets/${asset.id}/`);
      setMsg('Asset deleted.'); setTimeout(()=>setMsg(''),3000); load();
    } catch { setError('Delete failed.'); }
  };

  const handleSave = async e => {
    e.preventDefault();
    if(!form.name)       { setError('Asset name is required.'); return; }
    if(!form.unit_price) { setError('Unit price is required.'); return; }
    setSaving(true); setError('');
    try {
      const cleanForm = {
        ...form,
        purchase_cost:     total > 0 ? total.toFixed(2) : null,
        subtotal:          subtotal > 0 ? subtotal.toFixed(2) : null,
        gst_amount:        gstAmt > 0 ? gstAmt.toFixed(2) : null,
        purchase_date:     form.purchase_date     || null,
        warranty_expiry:   form.warranty_expiry   || null,
        next_maintenance:  form.next_maintenance  || null,
        payment_date:      form.payment_date      || null,
      };
      delete cleanForm.also_record_expense;

      let savedAsset;
      if(selected?.id) {
        const res = await api.patch(`/assets/${selected.id}/`, cleanForm);
        savedAsset = res.data;
        setMsg('Asset updated!');
      } else {
        const res = await api.post('/assets/', cleanForm);
        savedAsset = res.data;
        setMsg('Asset added!');
      }

      if(form.also_record_expense && total > 0) {
        await api.post('/expenses/', {
          category: 'PURCHASE',
          date: form.purchase_date || today,
          amount: total.toFixed(2),
          payment_mode: form.payment_mode,
          reference_number: form.payment_reference,
          vendor_name: form.vendor_name,
          notes: `Asset Purchase: ${form.name} (${form.quantity} ${form.unit}) — ${savedAsset?.asset_number||''}`,
        }).catch(()=>{});
        setMsg('Asset saved and expense recorded!');
      }

      setShowModal(false); load();
      setTimeout(()=>setMsg(''), 4000);
    } catch(err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to save.');
    } finally { setSaving(false); }
  };

  const totalValue = assets.reduce((s,a)=>s+parseFloat(a.purchase_cost||0),0);
  const goodCount  = assets.filter(a=>a.condition==='GOOD').length;
  const dueCount   = assets.filter(a=>isDue(a.next_maintenance)).length;

  return (
    <Layout title="Asset Register" subtitle="Company assets and equipment management"
      actions={
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-secondary" onClick={()=>setShowReport(true)}><Printer size={14}/>Report</button>
          <button className="btn btn-primary" onClick={openAdd}><Plus size={14}/>Add Asset</button>
        </div>
      }
    >
    <style>{`
        @media print {
         .no-print { display:none!important; }
         .main-content { margin-left:0!important; }
         .sidebar,.topbar,.hamburger { display:none!important; }
         .page-content { padding:0!important; }
         .modal-overlay { position:static!important; background:none!important; }
         .modal { box-shadow:none!important; max-height:none!important; overflow:visible!important; width:100%!important; max-width:100%!important; }
         tfoot { display:table-footer-group; }
         div { overflow:visible!important; max-height:none!important; }
         #asset-report-content { max-height:none!important; overflow:visible!important; width:100%!important; position:fixed; top:0; left:0; }
         @page { margin:12mm; size:A4 landscape; }
        }
    `}</style>

      {msg   && <div className="success-box no-print">{msg}</div>}
      {error && <div className="error-box no-print">{error}</div>}

      {/* KPIs */}
      <div className="kpi-grid no-print" style={{marginBottom:16}}>
        <div className="kpi-card brand"><div className="kpi-value">{assets.length}</div><div className="kpi-label">Total Assets</div></div>
        <div className="kpi-card green"><div className="kpi-value">{goodCount}</div><div className="kpi-label">Good Condition</div></div>
        <div className="kpi-card red"><div className="kpi-value">{dueCount}</div><div className="kpi-label">Maintenance Due</div></div>
        <div className="kpi-card amber"><div className="kpi-value">{inr(totalValue)}</div><div className="kpi-label">Total Asset Value</div></div>
      </div>

      {dueCount > 0 && (
        <div className="no-print" style={{padding:'10px 16px',background:'#FDE8E8',borderRadius:'var(--radius-sm)',marginBottom:16,fontSize:13,color:'var(--red)',fontWeight:600}}>
          ⚠️ {dueCount} asset{dueCount>1?'s are':' is'} due for maintenance!
        </div>
      )}

      {/* Asset List */}
      <div className="card no-print">
        <div className="filter-bar" style={{marginBottom:16}}>
          <div className="search-wrap" style={{flex:2}}>
            <Search size={14}/>
            <input className="search-input" placeholder="Search by name, asset number..." value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <select className="form-input" style={{width:'auto',minWidth:150}} value={filterCat} onChange={e=>setFilterCat(e.target.value)}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c=><option key={c.val} value={c.val}>{c.icon} {c.label}</option>)}
          </select>
          <select className="form-input" style={{width:'auto',minWidth:130}} value={filterCond} onChange={e=>setFilterCond(e.target.value)}>
            <option value="">All Conditions</option>
            {CONDITIONS.map(c=><option key={c.val} value={c.val}>{c.label}</option>)}
          </select>
          {(filterCat||filterCond)&&<button className="btn btn-secondary btn-sm" onClick={()=>{setFilterCat('');setFilterCond('');}}>Clear</button>}
        </div>

        {loading ? <div className="loading"><div className="spinner"/>Loading...</div> :
          assets.length===0 ? <div className="empty-state"><p>No assets found. Click "Add Asset" to get started.</p></div> :
          <div style={{overflowX:'auto'}}>
            <table>
              <thead>
                <tr>
                  <th>Asset #</th><th>Name</th><th>Category</th><th>Qty</th><th>Unit</th>
                  <th>Condition</th><th>Location</th><th>Assigned To</th>
                  <th>Total Cost</th><th>Next Maintenance</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assets.map(asset=>{
                  const cat  = getCat(asset.category);
                  const cond = getCond(asset.condition);
                  const mainDue = isDue(asset.next_maintenance);
                  return (
                    <tr key={asset.id} style={{opacity:asset.is_active?1:0.6}}>
                      <td style={{color:'var(--brand)',fontWeight:600,fontFamily:'DM Mono,monospace',fontSize:12}}>{asset.asset_number}</td>
                      <td style={{fontWeight:600}}>{asset.name}</td>
                      <td><span style={{fontSize:12}}>{cat?.icon} {cat?.label}</span></td>
                      <td style={{fontFamily:'DM Mono,monospace',textAlign:'center'}}>{asset.quantity||1}</td>
                      <td style={{fontSize:12}}>{asset.unit||'NOS'}</td>
                      <td><span style={{fontSize:11,padding:'2px 8px',borderRadius:20,fontWeight:600,background:cond?.bg,color:cond?.color}}>{cond?.label}</span></td>
                      <td style={{fontSize:12}}>{getLoc(asset.location)?.label||asset.location}</td>
                      <td style={{fontSize:12,color:'var(--text-secondary)'}}>{asset.assigned_to||'—'}</td>
                      <td style={{fontFamily:'DM Mono,monospace',fontSize:12,fontWeight:600,color:'var(--brand)'}}>{asset.purchase_cost?inr(asset.purchase_cost):'—'}</td>
                      <td><span style={{fontSize:12,color:mainDue?'var(--red)':'var(--text-secondary)',fontWeight:mainDue?700:400}}>{asset.next_maintenance?`${mainDue?'⚠️ ':''}${asset.next_maintenance}`:'—'}</span></td>
                      <td>
                        <div style={{display:'flex',gap:6}}>
                          <button className="btn btn-sm btn-secondary" title="View" onClick={()=>{setSelected(asset);setShowView(true);}}><Eye size={12}/></button>
                          <button className="btn btn-sm btn-secondary" title="Edit" onClick={()=>openEdit(asset)}><Edit2 size={12}/></button>
                          <button className="btn btn-sm btn-danger" title="Delete" onClick={()=>handleDelete(asset)}><Trash2 size={12}/></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        }
      </div>

      {/* ── PRINTABLE REPORT ── */}
      {showReport && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowReport(false)}>
          <div id="asset-report-content" style={{background:'#fff',borderRadius:'var(--radius)',padding:24,width:'95vw',maxWidth:1100,maxHeight:'92vh',overflowY:'auto'}}>
            <div className="no-print" style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <h3 style={{fontSize:16,fontWeight:700}}>Asset Register Report</h3>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-primary btn-sm" onClick={()=>window.print()}>🖨 Print / PDF</button>
                <button className="btn btn-secondary btn-sm" onClick={()=>setShowReport(false)}>Close</button>
              </div>
            </div>

            {/* Company Header */}
            <div style={{borderBottom:'3px solid #1e3a5f',paddingBottom:16,marginBottom:20}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:16}}>
                <div style={{display:'flex',alignItems:'center',gap:16}}>
                  <img src="/TycheAbodes-logo1.png" alt="Logo" style={{width:70,height:70,objectFit:'contain',borderRadius:8}} onError={e=>e.target.style.display='none'}/>
                  <div>
                    <div style={{fontSize:18,fontWeight:800,color:'#1e3a5f',fontFamily:'Inter,sans-serif'}}>TYCHE ABODES PRIVATE LIMITED</div>
                    <div style={{fontSize:11,color:'#555',marginTop:3}}>No. 401, Ramani's Frazer Square, Near Head Post Office, Goods Shed Road, Coimbatore - 641001.</div>
                    <div style={{fontSize:11,color:'#555',marginTop:2}}>📞 +91 94433 23246 &nbsp;|&nbsp; GSTIN: 33AAKCT5967G1ZY</div>
                  </div>
                </div>
                <div className="asset-report-meta" style={{textAlign:'right'}}>
                  <div style={{fontSize:16,fontWeight:800,color:'#1e3a5f',textTransform:'uppercase',letterSpacing:1}}>Asset Register</div>
                  <div style={{fontSize:12,color:'#555',marginTop:4}}>Total Assets: <strong>{assets.length}</strong></div>
                  <div style={{fontSize:12,color:'#555',marginTop:2}}>Total Value: <strong>{inr(totalValue)}</strong></div>
                  <div style={{fontSize:12,color:'#555',marginTop:2}}>Generated: <strong>{new Date().toLocaleDateString('en-IN')}</strong></div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))',gap:0,border:'1px solid #ddd',borderRadius:8,overflow:'hidden',marginBottom:20}}>
              {[
                ['Total Assets', assets.length, '#1e3a5f'],
                ['Good Condition', goodCount, '#3B6D11'],
                ['Maintenance Due', dueCount, '#C0392B'],
                ['Total Value', inr(totalValue), '#854F0B'],
              ].map(([l,v,c],i)=>(
                <div key={i} style={{padding:'12px 16px',textAlign:'center',background:i%2===0?'#f8f9fc':'#fff',borderRight:i<3?'1px solid #ddd':'none'}}>
                  <div style={{fontSize:22,fontWeight:700,color:c,fontFamily:'DM Mono,monospace'}}>{v}</div>
                  <div style={{fontSize:10,color:'#666',textTransform:'uppercase',letterSpacing:'0.5px',marginTop:4}}>{l}</div>
                </div>
              ))}
            </div>

            {/* Asset Table */}
            <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10,marginBottom:20}}>
              <thead>
                <tr style={{background:'#1e3a5f',color:'#fff'}}>
                  {['Asset #','Name','Category','Qty','Unit','Condition','Location','Assigned To','Unit Price','GST%','Total Cost','Purchase Date','Next Maintenance'].map(h=>(
                    <th key={h} style={{padding:'6px 8px',textAlign:'left',fontWeight:600,fontSize:9,whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {assets.map((asset,i)=>{
                  const cat  = getCat(asset.category);
                  const cond = getCond(asset.condition);
                  const mainDue = isDue(asset.next_maintenance);
                  return (
                    <tr key={asset.id} style={{background:i%2===0?'#f8f9fc':'#fff',borderBottom:'1px solid #eee'}}>
                      <td style={{padding:'5px 8px',color:'#1e3a5f',fontWeight:700,fontFamily:'DM Mono,monospace'}}>{asset.asset_number}</td>
                      <td style={{padding:'5px 8px',fontWeight:600}}>{asset.name}</td>
                      <td style={{padding:'5px 8px'}}>{cat?.icon} {cat?.label}</td>
                      <td style={{padding:'5px 8px',textAlign:'center',fontFamily:'DM Mono,monospace'}}>{asset.quantity||1}</td>
                      <td style={{padding:'5px 8px'}}>{asset.unit||'NOS'}</td>
                      <td style={{padding:'5px 8px'}}><span style={{background:cond?.bg,color:cond?.color,padding:'1px 6px',borderRadius:10,fontSize:9,fontWeight:700}}>{cond?.label}</span></td>
                      <td style={{padding:'5px 8px'}}>{getLoc(asset.location)?.label}</td>
                      <td style={{padding:'5px 8px'}}>{asset.assigned_to||'—'}</td>
                      <td style={{padding:'5px 8px',fontFamily:'DM Mono,monospace'}}>{asset.unit_price?inr(asset.unit_price):'—'}</td>
                      <td style={{padding:'5px 8px',textAlign:'center'}}>{asset.gst_percent||0}%</td>
                      <td style={{padding:'5px 8px',fontFamily:'DM Mono,monospace',fontWeight:700,color:'#1e3a5f'}}>{asset.purchase_cost?inr(asset.purchase_cost):'—'}</td>
                      <td style={{padding:'5px 8px',fontFamily:'DM Mono,monospace'}}>{asset.purchase_date||'—'}</td>
                      <td style={{padding:'5px 8px',color:mainDue?'#C0392B':'inherit',fontWeight:mainDue?700:400}}>{asset.next_maintenance?`${mainDue?'⚠️ ':''}${asset.next_maintenance}`:'—'}</td>
                    </tr>
                  );
                })}
                </tbody>
                <tfoot>
                <tr style={{background:'#1e3a5f',color:'#fff',fontWeight:700}}>
                  <td colSpan={10} style={{padding:'7px 8px',fontSize:11}}>TOTAL ASSET VALUE</td>
                  <td style={{padding:'7px 8px',fontFamily:'DM Mono,monospace',fontSize:12}}>{inr(totalValue)}</td>
                  <td colSpan={2}/>
                </tr>
              </tfoot>
              
            </table>
            </div>
            <div style={{marginTop:12, padding:'10px 16px', background:'#1e3a5f', color:'#fff', borderRadius:6, display:'flex', justifyContent:'space-between', alignItems:'center', pageBreakInside:'avoid', pageBreakBefore:'avoid'}}>
                <span style={{fontSize:13, fontWeight:700, letterSpacing:1}}>TOTAL ASSET VALUE</span>
                <span style={{fontSize:16, fontWeight:800, fontFamily:'DM Mono,monospace'}}>{inr(totalValue)}</span>
            </div>

            {/* Footer */}
            <div style={{borderTop:'2px solid #1e3a5f',paddingTop:10,marginTop:16,display:'flex',justifyContent:'space-between',fontSize:10,color:'#666'}}>
              <span>TYCHE ABODES PRIVATE LIMITED — Confidential</span>
              <span>Generated: {new Date().toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal" style={{maxWidth:680,maxHeight:'92vh',overflowY:'auto'}}>
            <div className="modal-header">
              <h3>{selected?`Edit Asset — ${selected.asset_number}`:'Add New Asset'}</h3>
              <button onClick={()=>setShowModal(false)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18}}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                {error && <div className="error-box">{error}</div>}

                <ST>Asset Information</ST>
                <div className="form-grid">
                  <Field label="Asset Name *" full>
                    <input className="form-input" required value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. Spanner Set, Grease Gun"/>
                  </Field>
                  <Field label="Category *">
                    <select className="form-input" value={form.category} onChange={e=>set('category',e.target.value)}>
                      {CATEGORIES.map(c=><option key={c.val} value={c.val}>{c.icon} {c.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Description">
                    <input className="form-input" value={form.description||''} onChange={e=>set('description',e.target.value)} placeholder="Brief description"/>
                  </Field>
                  <Field label="Condition">
                    <select className="form-input" value={form.condition} onChange={e=>set('condition',e.target.value)}>
                      {CONDITIONS.map(c=><option key={c.val} value={c.val}>{c.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Location">
                    <select className="form-input" value={form.location} onChange={e=>set('location',e.target.value)}>
                      {LOCATIONS.map(l=><option key={l.val} value={l.val}>{l.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Assigned To">
                    <select className="form-input" value={form.assigned_to||''} onChange={e=>set('assigned_to',e.target.value)}>
                      <option value="">Select Employee / Department</option>
                      <option value="General">General</option>
                      {employees.map(e=><option key={e.id} value={e.full_name}>{e.full_name} — {e.designation}</option>)}
                    </select>
                  </Field>
                  <Field label="Status">
                    <select className="form-input" value={form.is_active?'true':'false'} onChange={e=>set('is_active',e.target.value==='true')}>
                      <option value="true">Active</option>
                      <option value="false">Inactive / Disposed</option>
                    </select>
                  </Field>
                </div>

                <ST>Purchase Details</ST>
                <div className="form-grid">
                  <Field label="Quantity *">
                    <input className="form-input" type="number" min="1" value={form.quantity} onChange={e=>set('quantity',e.target.value)}/>
                  </Field>
                  <Field label="Unit">
                    <select className="form-input" value={form.unit} onChange={e=>set('unit',e.target.value)}>
                      {UNITS.map(u=><option key={u}>{u}</option>)}
                    </select>
                  </Field>
                  <Field label="Unit Price (₹) *">
                    <input className="form-input" type="number" step="0.01" value={form.unit_price} onChange={e=>set('unit_price',e.target.value)} placeholder="0.00"/>
                  </Field>
                  <Field label="GST %">
                    <select className="form-input" value={form.gst_percent} onChange={e=>set('gst_percent',e.target.value)}>
                      {GST_RATES.map(r=><option key={r} value={r}>{r}%</option>)}
                    </select>
                  </Field>
                  <Field label="Purchase Date">
                    <input className="form-input" type="date" value={form.purchase_date||''} onChange={e=>set('purchase_date',e.target.value)} max={today}/>
                  </Field>
                  <Field label="Vendor / Supplier">
                    <input className="form-input" value={form.vendor_name||''} onChange={e=>set('vendor_name',e.target.value)} placeholder="Supplier name"/>
                  </Field>
                </div>

                {/* Auto Calculation */}
                {unitPrice > 0 && (
                  <div style={{background:'var(--brand-dim)',borderRadius:'var(--radius-sm)',padding:14,marginBottom:14,border:'1px solid var(--border)'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--brand)',textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:10}}>Amount Calculation</div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(90px, 1fr))',gap:12}}>
                      {[['Qty',`${qty} ${form.unit}`],['Unit Price',inr(unitPrice)],['Subtotal',inr(subtotal)],['GST',`${inr(gstAmt)} (${gstPct}%)`],['Total',inr(total)]].map(([l,v])=>(
                        <div key={l}>
                          <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',marginBottom:3}}>{l}</div>
                          <div style={{fontSize:13,fontWeight:700,color:'var(--brand)',fontFamily:'DM Mono,monospace'}}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <ST>Payment Details</ST>
                <div className="form-grid">
                  <Field label="Payment Mode">
                    <select className="form-input" value={form.payment_mode} onChange={e=>set('payment_mode',e.target.value)}>
                      {PAYMENT_MODES.map(m=><option key={m}>{m}</option>)}
                    </select>
                  </Field>
                  <Field label="Payment Date">
                    <input className="form-input" type="date" value={form.payment_date||''} onChange={e=>set('payment_date',e.target.value)} max={today}/>
                  </Field>
                  {(form.payment_mode==='BANK'||form.payment_mode==='CHEQUE'||form.payment_mode==='UPI')&&(
                    <Field label="Reference Number" full>
                      <input className="form-input" value={form.payment_reference||''} onChange={e=>set('payment_reference',e.target.value)} placeholder="Cheque/UTR/UPI reference"/>
                    </Field>
                  )}
                </div>

                <ST>Maintenance & Warranty</ST>
                <div className="form-grid">
                  <Field label="Warranty Expiry Date">
                    <input className="form-input" type="date" value={form.warranty_expiry||''} onChange={e=>set('warranty_expiry',e.target.value)}/>
                  </Field>
                  <Field label="Next Maintenance Date">
                    <input className="form-input" type="date" value={form.next_maintenance||''} onChange={e=>set('next_maintenance',e.target.value)}/>
                  </Field>
                  <Field label="Notes" full>
                    <textarea className="form-input" rows={2} value={form.notes||''} onChange={e=>set('notes',e.target.value)} placeholder="Additional notes"/>
                  </Field>
                </div>

                {!selected && (
                  <div style={{marginTop:8,padding:'12px 14px',background:'var(--amber-bg)',border:'1px solid var(--amber-border)',borderRadius:'var(--radius-sm)'}}>
                    <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',fontSize:13,fontWeight:500,color:'var(--amber)'}}>
                      <input type="checkbox" checked={form.also_record_expense} onChange={e=>set('also_record_expense',e.target.checked)} style={{width:16,height:16}}/>
                      Also record {total>0?inr(total):'amount'} as an Expense (Purchase / Materials)
                    </label>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Saving...':'Save Asset'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showView && selected && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowView(false)}>
          <div className="modal" style={{maxWidth:560,maxHeight:'90vh',overflowY:'auto'}}>
            <div className="modal-header">
              <div>
                <h3>{selected.name}</h3>
                <p style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>{selected.asset_number} · {getCat(selected.category)?.icon} {getCat(selected.category)?.label}</p>
              </div>
              <button onClick={()=>setShowView(false)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18}}>×</button>
            </div>
            <div className="modal-body">
              {[
                {title:'Asset Information', rows:[
                  ['Asset Number',selected.asset_number],
                  ['Name',selected.name],
                  ['Category',`${getCat(selected.category)?.icon} ${getCat(selected.category)?.label}`],
                  ['Description',selected.description||'—'],
                  ['Condition',getCond(selected.condition)?.label],
                  ['Location',getLoc(selected.location)?.label],
                  ['Assigned To',selected.assigned_to||'—'],
                  ['Status',selected.is_active?'Active':'Inactive'],
                ]},
                {title:'Purchase Details', rows:[
                  ['Quantity',`${selected.quantity||1} ${selected.unit||'NOS'}`],
                  ['Unit Price',selected.unit_price?inr(selected.unit_price):'—'],
                  ['GST',`${selected.gst_percent||0}%`],
                  ['GST Amount',selected.gst_amount?inr(selected.gst_amount):'—'],
                  ['Total Cost',selected.purchase_cost?inr(selected.purchase_cost):'—'],
                  ['Purchase Date',selected.purchase_date||'—'],
                  ['Vendor',selected.vendor_name||'—'],
                ]},
                {title:'Payment Details', rows:[
                  ['Payment Mode',selected.payment_mode||'—'],
                  ['Payment Date',selected.payment_date||'—'],
                  ['Reference #',selected.payment_reference||'—'],
                ]},
                {title:'Maintenance & Warranty', rows:[
                  ['Warranty Expiry',selected.warranty_expiry?`${selected.warranty_expiry}${isExpired(selected.warranty_expiry)?' ⚠️ EXPIRED':''}`:'—'],
                  ['Next Maintenance',selected.next_maintenance?`${selected.next_maintenance}${isDue(selected.next_maintenance)?' ⚠️ DUE':''}`:'—'],
                  ['Notes',selected.notes||'—'],
                ]},
              ].map((sec,si)=>(
                <div key={si} style={{marginBottom:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--brand)',textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:8,borderBottom:'1px solid var(--border)',paddingBottom:4}}>{sec.title}</div>
                  {sec.rows.map(([l,v])=>(
                    <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid var(--border-light)'}}>
                      <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:700,textTransform:'uppercase',minWidth:160}}>{l}</span>
                      <span style={{fontSize:13,color:'var(--text-primary)',textAlign:'right'}}>{v}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowView(false)}>Close</button>
              <button className="btn btn-primary" onClick={()=>{setShowView(false);openEdit(selected);}}>Edit</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}